<template>
  <div data-app>
    <v-card>
      <v-card-title>
        {{ cardTitle }}
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="vehicleCollection"
        :search="search"
        :sort-by="['name']"
        :loading="loadingTable"
        :items-per-page="10"
        @click:row="handleClickItem"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <VehicleModalForm
              :modalData="modalData"
              :permissions="permissions"
              @closeModalForm="handleCloseModalForm"
              @saveModalForm="handleSaveModalForm"
              @new="handleNew"
            />
            <v-spacer />
            <DeleteModalDialog
              :dialogDelete="dialogDelete"
              @closeDelete="handleCloseDelete"
              @deleteItemConfirm="handleDeleteItemConfirm"
            />
          </v-toolbar>
        </template>

        <template v-slot:item.asset_type="{ item }">
          <v-chip small :color="item.asset_type === 'drone' ? 'blue lighten-4' : 'grey lighten-3'">
            <v-icon small class="mr-1">
              {{ item.asset_type === 'drone' ? 'mdi-quadcopter' : 'mdi-car' }}
            </v-icon>
            {{ item.asset_type === 'drone' ? 'Drone' : 'Vehicle' }}
          </v-chip>
        </template>

        <template v-slot:item.license_number="{ item }">
          <span>{{ item.license_number || '-' }}</span>
        </template>

        <template v-slot:item.mot_validity_date="{ item }">
          <span v-if="item.asset_type !== 'drone'">
            {{ item.mot_validity_date || '-' }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.odometer_km="{ item }">
          <span v-if="item.asset_type !== 'drone' && item.odometer_km !== null">
            {{ formatKm(item.odometer_km) }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.km_to_service="{ item }">
          <span v-if="item.asset_type !== 'drone' && item.km_to_service !== null">
            {{ formatKm(item.km_to_service) }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon
            v-if="permissionCan('delete') && item.deletable"
            small
            @click.stop="deleteItem(item)"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import DeleteModalDialog from "@/view/components/DeleteModalDialog";
import { listModelsMixins } from "@/view/mixins/listModelsMixins.js";
import VehicleModalForm, { initialFormData } from "./VehicleModalForm";

export const ENDPOINT = "transport/vehicle/";
export const PERMISSION_TO = "transport.vehicle.";

export default {
  name: "Vehicles",
  components: { VehicleModalForm, DeleteModalDialog },
  mixins: [listModelsMixins],
  data() {
    return {
      routeName: "vehicles",
      routePath: "/transport/vehicles/",
      permissionTo: PERMISSION_TO,
      cardTitle: "Vehicles and drones",
      search: "",
      permissions: JSON.parse(localStorage.permissions),
      headers: [
        { text: "Type", value: "asset_type" },
        { text: "Asset", value: "name" },
        { text: "License / registration / serial", value: "license_number" },
        { text: "MOT validity date", value: "mot_validity_date" },
        { text: "Current KM", value: "odometer_km" },
        { text: "KM to next service", value: "km_to_service" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      modalData: { dialog: false, editedId: null },
      editedItem: initialFormData(),
      dialogDelete: false,
      itemToDelete: null,
      loadingTable: true,
    };
  },
  computed: {
    ...mapGetters(["vehicleCollection"]),
  },
  watch: {
    dialogDelete(value) {
      if (!value) this.handleCloseDelete();
    },
    "$route.params.id": {
      deep: true,
      immediate: true,
      handler(id) {
        if (id) this.$nextTick(() => this.editItem(id));
      },
    },
  },
  methods: {
    ...mapActions(["fetchVehicle"]),
    formatKm(value) {
      return `${Number(value).toLocaleString("hu-HU")} km`;
    },
    fetchModel() {
      return this.fetchVehicle();
    },
    deleteItem(item) {
      this.itemToDelete = item;
      this.dialogDelete = true;
    },
    handleCloseDelete() {
      this.dialogDelete = false;
      this.itemToDelete = null;
      this.loadingDelete = false;
    },
    async handleSaveModalForm(savedVehicle) {
      this.loadingTable = true;
      try {
        await this.fetchModel();
        if (savedVehicle && savedVehicle.id) {
          this.modalData.dialog = true;
          this.modalData.editedId = savedVehicle.id;
          this.editedItem = { ...initialFormData(), ...savedVehicle };
        }
      } catch (error) {
        console.error("Vehicle list refresh failed:", error);
      } finally {
        this.loadingTable = false;
      }
    },
    async handleDeleteItemConfirm() {
      if (!this.itemToDelete || !this.itemToDelete.id) return;

      this.loadingDelete = true;
      try {
        await ApiService.delete(ENDPOINT + this.itemToDelete.id);
        this.loadingTable = true;
        await this.fetchModel();
      } catch (error) {
        console.error("Vehicle delete failed:", error);
      } finally {
        this.loadingTable = false;
        this.handleCloseDelete();
      }
    },
  },
  mounted() {
    this.fetchModel().then(() => { this.loadingTable = false; });
  },
};
</script>
